/**
 * Save + change detection for Edit Sent form.
 */

import { useCallback, useMemo } from 'react';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import {
  shortlinkPick,
  type ClickIdStructureValue,
  type DomainSchemeValue,
} from '@/features/smswiser-campaigns/utils/shortlinkOptionState';

import type { EditSentDropPayload } from './EditSentDropModal.types';
import { buildSentDropPayloads } from './buildSentDropPayloads';
import type { EditSentDropCreativeAiApi } from './useEditSentDropCreativeAiState';

const LEAVE = '__unchanged__';

interface SaveArgs {
  drops: PreparedDrop[];
  isBulk: boolean;
  wiserId: number;
  title: string;
  routeId: number | '';
  tldValue: string;
  forceSend: boolean | 'unchanged';
  clickIdStructure: ClickIdStructureValue;
  domainScheme: DomainSchemeValue;
  selectedSenderNames: string[];
  selectedSmsTexts: string[];
  onSave: (p: EditSentDropPayload | EditSentDropPayload[]) => void;
  onClose: () => void;
  creativeAi: EditSentDropCreativeAiApi;
}

export function useEditSentDropSave(a: SaveArgs) {
  const {
    drops,
    isBulk,
    wiserId,
    title,
    routeId,
    tldValue,
    forceSend,
    clickIdStructure,
    domainScheme,
    selectedSenderNames,
    selectedSmsTexts,
    onSave,
    onClose,
    creativeAi,
  } = a;

  const editableCount = useMemo(() => drops.filter((d) => d.sentDropId != null).length, [drops]);
  const hasPerDropCreative = useMemo(
    () =>
      (creativeAi.perDropSmsTexts?.some((b) => b.length > 0) ?? false) ||
      (creativeAi.perDropSenderNames?.some((b) => b.length > 0) ?? false),
    [creativeAi.perDropSmsTexts, creativeAi.perDropSenderNames],
  );
  // Like forceSend: a concrete pick is an edit (bulk starts at 'unchanged').
  const shortlinkPicked =
    shortlinkPick(clickIdStructure) !== undefined || shortlinkPick(domainScheme) !== undefined;
  const hasAnyChange =
    shortlinkPicked ||
    (isBulk &&
      (routeId !== '' ||
        (tldValue && tldValue !== LEAVE) ||
        selectedSenderNames.length > 0 ||
        selectedSmsTexts.length > 0 ||
        hasPerDropCreative ||
        forceSend === true ||
        forceSend === false)) ||
    (!isBulk &&
      ((selectedSenderNames.length > 0 && selectedSmsTexts.length > 0) ||
        forceSend === true ||
        forceSend === false));
  const canSave = editableCount > 0 && hasAnyChange;

  const handleSave = useCallback(() => {
    if (editableCount === 0) return;
    if (!isBulk && (selectedSenderNames.length === 0 || selectedSmsTexts.length === 0)) return;
    const payloads = buildSentDropPayloads({
      drops,
      isBulk,
      title,
      selectedSenderNames,
      selectedSmsTexts,
      wiserId,
      routeId,
      tldValue,
      forceSend,
      useOnlyNewDomains: 'unchanged',
      clickIdStructure,
      domainScheme,
      perDropSmsTexts: creativeAi.perDropSmsTexts,
      perDropSenderNames: creativeAi.perDropSenderNames,
    });
    if (payloads.length > 0) onSave(isBulk ? payloads : payloads[0]!);
    onClose();
  }, [
    drops,
    isBulk,
    onClose,
    onSave,
    routeId,
    selectedSenderNames,
    selectedSmsTexts,
    title,
    tldValue,
    wiserId,
    editableCount,
    forceSend,
    clickIdStructure,
    domainScheme,
    creativeAi.perDropSmsTexts,
    creativeAi.perDropSenderNames,
  ]);

  return { handleSave, editableCount, canSave };
}
